import { motion } from 'framer-motion';
import { Sun, Cloud, CloudRain, Wind, Droplets } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WeatherData } from '@/types';

interface WeatherWidgetProps {
  weather: WeatherData[];
  className?: string;
}

const weatherIcons: Record<string, typeof Sun> = {
  sun: Sun,
  cloud: Cloud,
  'cloud-rain': CloudRain,
};

export function WeatherWidget({ weather, className }: WeatherWidgetProps) {
  const today = weather[0];
  const forecast = weather.slice(1, 4);
  const WeatherIcon = weatherIcons[today?.icon || 'sun'] || Sun;

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-display flex items-center justify-between">
          <span>Meteorología</span>
          <span className="text-sm font-normal text-muted-foreground">
            {today?.municipality}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {today && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {/* Today's weather */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-accent/10 to-accent/5">
              <div className="p-3 rounded-full bg-accent/20">
                <WeatherIcon className="w-8 h-8 text-accent" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Hoy</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-display font-bold">
                    {today.temperature.max}°
                  </span>
                  <span className="text-lg text-muted-foreground">
                    / {today.temperature.min}°
                  </span>
                </div>
                <p className="text-sm text-foreground/80">{today.description}</p>
              </div>
              <div className="text-right space-y-1">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Droplets className="w-3 h-3" />
                  <span>{today.humidity}%</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Wind className="w-3 h-3" />
                  <span>{today.windSpeed} km/h</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <CloudRain className="w-3 h-3" />
                  <span>{today.precipitationProbability}%</span>
                </div>
              </div>
            </div>

            {/* Forecast */}
            <div className="grid grid-cols-3 gap-2">
              {forecast.map((day, index) => {
                const Icon = weatherIcons[day.icon] || Sun;
                const date = new Date(day.date);
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-3 rounded-lg bg-muted/50 text-center"
                  >
                    <p className="text-xs text-muted-foreground mb-1">
                      {date.toLocaleDateString('es-ES', { weekday: 'short' })}
                    </p>
                    <Icon className="w-5 h-5 mx-auto text-accent mb-1" />
                    <p className="text-sm font-medium">
                      {day.temperature.max}° / {day.temperature.min}°
                    </p>
                  </motion.div>
                );
              })}
            </div>

            {/* Rain alert */}
            {today.precipitationProbability > 50 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-3 p-3 rounded-lg bg-info/10 border border-info/20"
              >
                <CloudRain className="w-5 h-5 text-info" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-info">
                    Probabilidad de lluvia alta
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Los triggers de lluvia podrían activarse
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
